/**
 * Shopify Storefront API source mode + shared metafield-value parsing.
 *
 * STOQ writes every dataset the theme app embed needs onto SHOP metafields
 * (namespace `restockrocket_<env>`). This module reads them back with ONE
 * GraphQL POST to the shop's own Storefront API endpoint — no read request
 * ever hits STOQ's servers — and normalizes the metafield payloads into the
 * canonical dataset shapes the client consumes (historically the STOQ embed
 * endpoint shapes; the parsers accept both):
 *
 * | key                            | metafield value                              | canonical value         |
 * |--------------------------------|----------------------------------------------|-------------------------|
 * | `settings`                     | base fields in the shop DEFAULT locale plus  | fields already          |
 * |                                | a `translations[locale]` map (+`cache`,      | translated to           |
 * |                                | `cached_at`, `translation_locale` extras)    | `?translation_locale=`  |
 * | `cachedSellingPlans`           | `{ plans, disabled_plan_ids, cached_at }`    | same                    |
 * | `selling_plans`                | bare plans array (legacy fallback when       | `{ plans,               |
 * |                                | `cachedSellingPlans` is absent)              |   disabled_plan_ids,    |
 * |                                |                                              |   cached_at }`          |
 * | `variant_preorder_limits`      | `{ variant_preorder_limits: {...}, ...meta }`| bare map                |
 * | `variant_shipping_texts`       | `{ variant_shipping_texts: {...}, ...meta }` | bare map                |
 *
 * The parsers below accept BOTH the wrapped metafield shape and the bare
 * canonical shape, and tolerate already-parsed values.
 */
import type {
  SellingPlan,
  SellingPlansResponse,
  StoqSettings,
  VariantPreorderLimits,
  VariantShippingTexts,
} from './types'
import { warn } from './util'

export const DEFAULT_STOREFRONT_API_VERSION = '2025-07'

export const PRODUCTION_METAFIELD_NAMESPACE = 'restockrocket_production'
export const STAGING_METAFIELD_NAMESPACE = 'restockrocket_staging'

/**
 * The dataset registry: one entry per shop metafield the SDK reads. `alias`
 * is the GraphQL alias (also the SSR-data field name), `key` the metafield
 * key. `marketScoped` datasets are also written under `<key>_for_market_<id>`
 * when Shopify Markets inventory scoping is enabled — the SDK prefers the
 * market key and falls back to the base key, like the theme embed.
 *
 * Variant availability (in-stock / out-of-stock-purchasable) is deliberately
 * NOT loaded from metafields — callers pass their own variant fields (see
 * ./variant-input.ts), which avoids fetching the catalog-sized id lists.
 */
const DATASETS: ReadonlyArray<{ alias: string; key: string; marketScoped?: boolean }> = [
  { alias: 'settings', key: 'settings' },
  // Preferred plans source: carries disabled_plan_ids; `selling_plans` is the
  // legacy bare-array fallback for shops that haven't re-synced yet.
  { alias: 'cachedSellingPlans', key: 'cachedSellingPlans' },
  { alias: 'sellingPlans', key: 'selling_plans' },
  { alias: 'variantShippingTexts', key: 'variant_shipping_texts', marketScoped: true },
  { alias: 'variantPreorderLimits', key: 'variant_preorder_limits', marketScoped: true },
]

/** Suffix for market-scoped aliases, e.g. `variantShippingTextsForMarket`. */
const MARKET_ALIAS_SUFFIX = 'ForMarket'

/**
 * Builds the shop-metafields query. With a `marketId` the query also selects
 * the `<key>_for_market_<id>` variants (aliased `<alias>ForMarket`), which
 * win over the base keys when present.
 */
export function buildMetafieldsQuery(marketId?: number): string {
  const select = (alias: string, key: string): string =>
    `    ${alias}: metafield(namespace: $namespace, key: "${key}") { value }`

  const fields = DATASETS.map(({ alias, key }) => select(alias, key))
  if (marketId !== undefined) {
    for (const { alias, key, marketScoped } of DATASETS) {
      if (marketScoped) {
        fields.push(select(`${alias}${MARKET_ALIAS_SUFFIX}`, `${key}_for_market_${marketId}`))
      }
    }
  }
  return `query StoqShopMetafields($namespace: String!) {\n  shop {\n${fields.join('\n')}\n  }\n}`
}

/** The exact query the SDK runs (without Markets scoping). */
export const STOREFRONT_METAFIELDS_QUERY = buildMetafieldsQuery()

/**
 * Derives the metafield namespace from the STOQ API host: staging hosts use
 * `restockrocket_staging`, everything else `restockrocket_production`.
 */
export function deriveNamespace(host: string): string {
  return /staging/i.test(host) ? STAGING_METAFIELD_NAMESPACE : PRODUCTION_METAFIELD_NAMESPACE
}

/** Thrown when the Storefront API query itself fails (network/token/GraphQL). */
export class StoqStorefrontApiError extends Error {
  /** HTTP status of the GraphQL response (0 when the response was 200 but carried GraphQL errors). */
  readonly status: number

  constructor(message: string, status = 0) {
    super(message)
    this.name = 'StoqStorefrontApiError'
    this.status = status
  }
}

export interface StorefrontQueryContext {
  /** myshopify domain, e.g. `my-store.myshopify.com`. */
  shop: string
  token: string
  apiVersion: string
  namespace: string
  marketId?: number
}

/** Raw metafield values by alias; `null` when the metafield does not exist. */
export type RawShopMetafields = Record<string, string | null>

interface GraphQLMetafieldNode {
  value?: string | null
}

interface GraphQLResponse<T> {
  data?: T | null
  errors?: Array<{ message?: string }>
}

/**
 * One GraphQL POST to `https://{shop}/api/{version}/graphql.json` (the shop's
 * own Storefront API endpoint). Returns the `data` payload; throws
 * {@link StoqStorefrontApiError} when the request fails as a whole (network,
 * bad token, invalid JSON, GraphQL errors with no data).
 */
export async function storefrontGraphql<T>(
  ctx: Pick<StorefrontQueryContext, 'shop' | 'token' | 'apiVersion'>,
  query: string,
  variables: Record<string, unknown>,
  label: string,
): Promise<T> {
  const url = `https://${ctx.shop}/api/${ctx.apiVersion}/graphql.json`
  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': ctx.token,
      },
      body: JSON.stringify({ query, variables }),
    })
  } catch (error) {
    throw new StoqStorefrontApiError(
      `[Stoq] Storefront API request to ${url} failed: ${String(error)}`,
    )
  }

  if (!response.ok) {
    throw new StoqStorefrontApiError(
      `[Stoq] Storefront API responded ${response.status} for ${url} — check that `
        + '`storefrontToken` is a valid Storefront API access token for this shop',
      response.status,
    )
  }

  let json: GraphQLResponse<T>
  try {
    json = (await response.json()) as GraphQLResponse<T>
  } catch {
    throw new StoqStorefrontApiError(`[Stoq] Storefront API returned invalid JSON for ${url}`)
  }

  if (json.data === undefined || json.data === null) {
    const detail = json.errors?.map((e) => e.message).filter(Boolean).join('; ')
    throw new StoqStorefrontApiError(
      `[Stoq] Storefront API ${label} query failed${detail ? `: ${detail}` : ''}`,
    )
  }
  return json.data
}

/**
 * ONE GraphQL POST reading all STOQ shop metafields. Throws
 * {@link StoqStorefrontApiError} when the query fails as a whole.
 */
export async function fetchShopMetafields(ctx: StorefrontQueryContext): Promise<RawShopMetafields> {
  const data = await storefrontGraphql<{ shop?: Record<string, GraphQLMetafieldNode | null> | null }>(
    ctx,
    buildMetafieldsQuery(ctx.marketId),
    { namespace: ctx.namespace },
    'metafields',
  )

  const shop = data.shop
  if (!shop) {
    throw new StoqStorefrontApiError('[Stoq] Storefront API metafields query returned no shop')
  }

  const raw: RawShopMetafields = {}
  for (const [alias, node] of Object.entries(shop)) {
    raw[alias] = node && typeof node.value === 'string' ? node.value : null
  }
  return raw
}

/**
 * Prefers the `<alias>ForMarket` value (set when a market id was configured
 * and STOQ wrote a market-scoped metafield) over the base alias — same
 * precedence as the theme embed's Liquid bootstrap.
 */
export function pickMetafieldValue(raw: RawShopMetafields, alias: string): string | null {
  return raw[`${alias}${MARKET_ALIAS_SUFFIX}`] ?? raw[alias] ?? null
}

// --- Parsers (shared by Storefront API mode and SSR data mode) -------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Accepts a raw metafield JSON string or an already-parsed value. Returns
 * `undefined` for null/undefined input or unparsable JSON (with a console
 * warning).
 */
export function parseMaybeJson(value: unknown, label: string): unknown {
  if (value === null || value === undefined) return undefined
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value) as unknown
  } catch {
    warn(`could not parse the "${label}" metafield value as JSON; continuing with defaults`)
    return undefined
  }
}

/**
 * Settings: the metafield stores base fields in the shop's DEFAULT locale
 * plus a per-locale `translations` map (only on multi-language shops). The
 * endpoint bakes translations in server-side, so this applies the same
 * overlay the theme embed's `applyTranslations` does: copy non-empty
 * translated values for the normalized locale over the base fields, then
 * drop the `translations` payload.
 *
 * Returns `undefined` when the value is missing or not an object — callers
 * decide whether that is fatal (it is at init: settings are required).
 */
export function parseSettingsValue(value: unknown, locale?: string): StoqSettings | undefined {
  const parsed = parseMaybeJson(value, 'settings')
  if (!isRecord(parsed)) return undefined

  const settings: StoqSettings = { ...parsed }
  const translations = settings['translations']
  delete settings['translations']

  if (settings['multi_language_enabled'] !== true || !isRecord(translations) || !locale) {
    return settings
  }

  const translated = translations[locale]
  if (isRecord(translated)) {
    for (const [key, translatedValue] of Object.entries(translated)) {
      if (translatedValue !== null && translatedValue !== undefined && translatedValue !== '') {
        settings[key] = translatedValue
      }
    }
  }
  return settings
}

/**
 * Selling plans: the `cachedSellingPlans` metafield carries the full
 * `{ plans, disabled_plan_ids, cached_at }` payload; the legacy
 * `selling_plans` metafield is a bare plans array. Accepts both.
 */
export function parseSellingPlansValue(value: unknown): SellingPlansResponse | undefined {
  const parsed = parseMaybeJson(value, 'selling_plans')
  if (Array.isArray(parsed)) {
    return { plans: parsed as SellingPlan[], disabled_plan_ids: [] }
  }
  if (isRecord(parsed) && Array.isArray(parsed['plans'])) {
    return {
      plans: parsed['plans'] as SellingPlan[],
      disabled_plan_ids: Array.isArray(parsed['disabled_plan_ids'])
        ? (parsed['disabled_plan_ids'] as number[])
        : [],
    }
  }
  return undefined
}

/**
 * Variant-keyed maps (shipping texts / preorder limits): metafields wrap the
 * map as `{ <nestedKey>: {...}, updated_at, ... }`. Also accepts the bare
 * map (variant-id keys are numeric, so a wrapper is unambiguous).
 */
export function parseVariantMapValue<T>(value: unknown, nestedKey: string): T | undefined {
  const parsed = parseMaybeJson(value, nestedKey)
  if (!isRecord(parsed)) return undefined
  if (isRecord(parsed[nestedKey])) return parsed[nestedKey] as T
  // A wrapper whose nested payload is missing/invalid is still a wrapper.
  if (nestedKey in parsed || 'updated_at' in parsed) return undefined
  return parsed as T
}

/** Convenience typed wrappers used by the client. */
export function parseShippingTextsValue(value: unknown): VariantShippingTexts | undefined {
  return parseVariantMapValue<VariantShippingTexts>(value, 'variant_shipping_texts')
}

export function parsePreorderLimitsValue(value: unknown): VariantPreorderLimits | undefined {
  return parseVariantMapValue<VariantPreorderLimits>(value, 'variant_preorder_limits')
}
